/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import { authState } from '../../store/auth';
import { userState } from '../../store/user';
import { livePreviewState } from '../../store/live-preview';

declare global {
  interface Window {
    axw: any;
  }
}

/**
 * Identity bridge for the Axon Webchat widget.
 *
 * The widget itself is loaded by the embed snippet in public/index.html — this
 * component only tells it who the visitor is. Commands are safe to fire at any
 * time: before the loader finishes downloading they are queued by the shim that
 * the snippet defines ahead of the script tag.
 *
 * The `user_hash` is an HMAC the backend computes over
 * `id \n email \n countryCode+phoneNumber \n exp` (exp always "") and returns on
 * the auth response — we only pass through the exact same raw strings it signed,
 * never recompute anything here (the verify secret must never reach the browser).
 *
 * Any mismatch is silent: Axon drops the identity and opens an anonymous chat. So
 * the phone is the two fields joined with nothing added, a missing value is "", and
 * the effect re-boots whenever a signed field changes (a profile edit brings a new
 * hash with it — see EnterEmailModal and EditTeamMembersModal).
 */
const AxonWebchat = () => {
  const { token } = useRecoilValue(authState);
  const user = useRecoilValue(userState);
  const isLivePreview = useRecoilValue(livePreviewState) === 'true';
  const booted = useRef(false);
  const clearedForPreview = useRef(false);

  const email = user?.email ?? '';
  const phone = `${user?.countryCode ?? ''}${user?.phoneNumber ?? ''}`;
  const name = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();

  useEffect(() => {
    if (typeof window.axw !== 'function') return;

    // An admin impersonating a company must never chat as that customer: anything
    // typed would reach support as them, verified. So no identity at all during live
    // preview — and one shutdown, so the widget can't resume a session it stored
    // earlier in this browser.
    if (isLivePreview) {
      if (booted.current || !clearedForPreview.current) {
        window.axw('shutdown');
        booted.current = false;
        clearedForPreview.current = true;
      }
      return;
    }
    clearedForPreview.current = false;

    if (token && user?.id && !user?.userHash) {
      // Every token-issuing response should carry one; the backend asked to hear
      // when it doesn't. No identity values here — just which user id.
      console.warn(
        `[webchat] No userHash for user ${user.id}; chat stays anonymous. Report to backend.`,
      );
    }

    if (token && user?.id && user?.userHash) {
      window.axw('boot', {
        user_id: user.id,
        user_hash: user.userHash,
        email,
        phone,
        name,
        exp: '',
      });
      booted.current = true;
    } else if (booted.current) {
      // Clears the visitor's session from the widget's own storage. This is
      // client-side only — there is no server-side session to revoke.
      window.axw('shutdown');
      booted.current = false;
    }
  }, [token, user?.id, user?.userHash, email, phone, name, isLivePreview]);

  return null;
};

export default AxonWebchat;
